import React from 'react';
import { useTranslation } from 'react-i18next';
import { Map, List } from 'lucide-react';
import { useAnalytics } from '../../hooks/useAnalytics';
import { useSection } from '../../hooks/useSection';
import { getTranslatedText as translate } from '../../utils/translationHelpers';

interface ViewToggleProps {
  view: 'map' | 'list';
  onChange: (view: 'map' | 'list') => void;
  config: any;
  translations: Record<string, Record<string, string>>;
  sectionType: string;
}

export const ViewToggle: React.FC<ViewToggleProps> = ({
  view,
  onChange,
  config,
  translations,
  sectionType
}) => {
  const { i18n } = useTranslation();
  const section = useSection();

  const analytics = useAnalytics({
    sectionId: section.id,
    sectionName: section.names.fr,
    sectionType
  });

  const handleViewChange = (newView: 'map' | 'list') => {
    analytics.trackEvent('view', 'view_change', {
      previous_view: view,
      new_view: newView
    });
    onChange(newView);
  };

  const translatedMap = translate(translations, 'view_map', i18n.language);
  const translatedList = translate(translations, 'view_list', i18n.language);

  return (
    <div
      className="flex bg-white rounded-full overflow-hidden shadow-lg"
      style={{ padding: '4px' }}
    >
      <button
        onClick={() => handleViewChange('map')}
        className="flex items-center px-4 sm:px-6 py-1.5 sm:py-2 rounded-full transition-all duration-200"
        style={{
          backgroundColor: view === 'map' ? config['major-color'] : 'transparent',
          color: view === 'map' ? '#fff' : '#000'
        }}
        aria-pressed={view === 'map'}
        aria-label={translatedMap}
      >
        <Map size={16} className="mr-1.5 sm:mr-2" />
        <span className="text-xs sm:text-sm font-medium">{translatedMap}</span>
      </button>
      <button
        onClick={() => handleViewChange('list')}
        className="flex items-center px-4 sm:px-6 py-1.5 sm:py-2 rounded-full transition-all duration-200"
        style={{
          backgroundColor: view === 'list' ? config['major-color'] : 'transparent',
          color: view === 'list' ? '#fff' : '#000'
        }}
        aria-pressed={view === 'list'}
        aria-label={translatedList}
      >
        <List size={16} className="mr-1.5 sm:mr-2" />
        <span className="text-xs sm:text-sm font-medium">{translatedList}</span>
      </button>
    </div>
  );
};
